import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Menu, type LucideIcon } from 'lucide-react';
import { AppSidebar, type SidebarBrand } from './AppSidebar';
import { SIDEBAR_FOOT_ID, SIDEBAR_SLOT_ID } from './sidebarSlot';
import { AppTitle } from './AppTitle';
import type { SidebarAuth } from './ProfileMenu';

// Whether the desktop rail is collapsed. Persisted so a reader who hides it to go
// full-screen on the document does not have to redo that every reload.
const RAIL_KEY = 'notice-rail-open';

export interface TabNavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  // Draw a thin divider above this item (e.g. to set off an admin-only section).
  dividerBefore?: boolean;
  // Draw a thin divider below this item (e.g. to close off a trailing section).
  dividerAfter?: boolean;
}

// A tab's look. Exported for the rail's other entries (the Blender add-on button), which read as
// one more line of the rail; `size` for those, smaller than the tabs.
export function navClass(active: boolean, size = 'text-sm'): string {
  return [
    `flex items-center gap-3 rounded-lg px-3 py-2 ${size} font-medium transition-colors`,
    active
      // OPAQUE: blue-500 at 15% over the page's #12294b, worked out — translucent, the page's
      // grid lines showed through the selected item.
      ? 'bg-[#18365f] text-blue-300'
      : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200',
  ].join(' ');
}

// Sidebar shell for the app: a fixed left rail on desktop, and a collapsible
// slide-out drawer on mobile (opened from the top bar's menu button) so the tab
// list never overflows the top of the screen. The drawer reuses the same
// AppSidebar — including its profile menu — so the mobile bar doesn't need to
// duplicate avatar/sign-out.
//   title       — app title shown in the header (drives AppTitle)
//   nav         — the app's tabs (label already resolved/translated)
//   contentClassName — extra classes on <main> (e.g. a themed background); the
//                      rail/drawer and the bars stay dark either way
export function TabbedSidebarLayout({
  title,
  nav,
  langToggle = false,
  contentClassName = '',
  brand,
  auth,
  children,
}: {
  title: string;
  nav: TabNavItem[];
  // Show the FR/EN toggle in the sidebar.
  langToggle?: boolean;
  contentClassName?: string;
  // The top-left brand link (rail, drawer, and mobile bar).
  brand: SidebarBrand;
  // The NoticeIA profile session.
  auth: SidebarAuth;
  children?: ReactNode;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [railOpen, setRailOpen] = useState(() => localStorage.getItem(RAIL_KEY) !== 'closed');
  useEffect(() => {
    localStorage.setItem(RAIL_KEY, railOpen ? 'open' : 'closed');
  }, [railOpen]);

  // Same tab list for the desktop rail and the mobile drawer; `onNavigate` lets
  // the drawer close itself when a tab is tapped.
  const navList = (onNavigate?: () => void) => (
    <nav className="flex flex-col gap-1">
      {nav.map(({ to, label, icon: Icon, end, dividerBefore, dividerAfter }) => (
        <div key={to} className="contents">
          {dividerBefore && <hr className="my-2 border-slate-600" />}
          <NavLink
            to={to}
            end={end}
            onClick={onNavigate}
            className={({ isActive }) => navClass(isActive)}
          >
            <Icon className="h-4 w-4" />
            {label}
          </NavLink>
          {dividerAfter && <hr className="my-2 border-slate-600" />}
        </div>
      ))}
    </nav>
  );

  return (
    <div
      // WHAT A SANDBOX SAVE PHOTOGRAPHS: the app, whole — the rail, the title bar and the
      // page, at the width of the window. It used to be the reader's own column (max-w-6xl),
      // which is the part that changes, and a record of a DESIGN that leaves out the frame
      // round it is a record of half the design. See sandboxSaves, and SandboxSaveWalk,
      // which walks the phase bar taking one of these per phase.
      //
      // One attribute in the shared shell, meaning nothing to the two builds that never
      // save: the sandbox is the only one with a Saves tab (IS_SANDBOX).
      data-capture-root=""
      className="flex min-h-dvh text-slate-100"
    >
      {/* Desktop rail — width animates to 0 rather than unmounting, so the collapse
          is a slide rather than a jump-cut, and the rail's own scroll position/state
          survives being hidden.

          STICKY ON THIS WRAPPER, not on the rail inside it. The rail was the sticky one,
          and never stuck: `overflow-hidden` here (which the collapse needs, to clip the
          rail as the width shrinks) makes this div the box a sticky child sticks within,
          and this div does not scroll — so the rail went up with the page and the page
          went on without it. The wrapper sticks to the window instead, a screen tall and
          held at the top of the row rather than stretched down it.

          The separator is the rail's own right edge, so a collapsed rail takes it along
          instead of leaving a line at the edge of the screen — in the colour of the dividers
          inside the rail, so the two lines read as the same rule. */}
      <div
        className={`sticky top-0 hidden h-dvh shrink-0 self-start overflow-hidden transition-[width] duration-200 ease-in-out md:block ${
          railOpen ? 'w-56' : 'w-0'
        }`}
      >
        <AppSidebar
          className="flex h-dvh w-56 border-r border-slate-600" langToggle={langToggle} brand={brand} auth={auth}
          foot={<div id={SIDEBAR_FOOT_ID} className="w-full" />}
        >
          {navList()}
          {/* Filled by the page that has something open — see sidebarSlot. */}
          <div id={SIDEBAR_SLOT_ID} />
        </AppSidebar>
      </div>

      {/* Collapse/expand handle for the desktop rail — reading the document full-screen
          is the point, and the rail is the one thing between a page and the whole width
          of the screen. Fixed to the viewport (not the flex flow) so it stays put as
          `left` transitions between the rail's edge and the screen's, rather than being
          pushed around by the width it is animating.

          INSIDE THE RAIL while it is open, against its right edge — it straddled the border,
          half on the rail and half over the page, and belonged to neither. At the TOP either
          way, level with the brand, where a hand going for the rail's edge looks first. */}
      <button
        type="button"
        onClick={() => setRailOpen((o) => !o)}
        aria-label={railOpen ? 'Collapse sidebar' : 'Expand sidebar'}
        title={railOpen ? 'Collapse sidebar' : 'Expand sidebar'}
        style={{ left: railOpen ? '12rem' : '0.5rem' }}
        className="fixed top-5 z-40 hidden rounded-full border border-slate-300 bg-white p-1 text-slate-500 shadow-md transition-[left] duration-200 ease-in-out hover:text-slate-900 md:flex"
      >
        {railOpen ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </button>

      {/* Mobile collapsible drawer (backdrop + sliding rail) */}
      <div className={`fixed inset-0 z-50 md:hidden ${menuOpen ? '' : 'pointer-events-none'}`}>
        <div
          onClick={() => setMenuOpen(false)}
          className={`absolute inset-0 bg-black/60 transition-opacity ${
            menuOpen ? 'opacity-100' : 'opacity-0'
          }`}
        />
        <div
          className={`absolute left-0 top-0 h-dvh transition-transform duration-200 ${
            menuOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          {/* OPAQUE, in the page's own blue: the rail is transparent by design (the blueprint
              under it is the page's, see index.css), but this one slides OVER the page, and
              transparent it was the page's tables and pills with the menu written across them. */}
          <AppSidebar langToggle={langToggle} brand={brand} auth={auth} className="flex h-dvh border-r border-slate-600 bg-[#12294b] shadow-2xl">
            {navList(() => setMenuOpen(false))}
          </AppSidebar>
          {/* The menu button stays put (same spot as the top bar's) so you can
              tap it again to collapse the drawer. */}
          <button
            type="button"
            onClick={() => setMenuOpen(false)}
            aria-label="Close menu"
            className="absolute left-3 top-3 rounded-lg p-1.5 text-slate-300 transition-colors hover:bg-slate-800 hover:text-slate-100"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile top bar: menu button (left), name (centered). Fixed light
            colors, not `dark:`-paired — this bar has no background of its own, so it
            sits straight on the blueprint page. */}
        <div className="relative flex items-center border-b border-slate-600 px-3 py-3 md:hidden">
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
            className="rounded-lg p-1.5 text-slate-300 transition-colors hover:bg-white/10 hover:text-slate-100"
          >
            <Menu className="h-5 w-5" />
          </button>
          <Link
            to={brand.to}
            aria-label={`${brand.label} home`}
            className="absolute left-1/2 -translate-x-1/2 text-lg font-bold tracking-wide transition-opacity hover:opacity-80"
          >
            {brand.label}
          </Link>
        </div>

        {/* Desktop title bar — same reasoning as the mobile bar above: no background
            of its own, fixed color rather than theme-paired. */}
        <div className="relative hidden border-b border-slate-600 px-6 py-5 md:block">
          <AppTitle title={title} />
        </div>

        <main className={`min-w-0 flex-1 ${contentClassName}`}>{children}</main>
      </div>
    </div>
  );
}
